import React, { useContext, useEffect, useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TrackingContext } from '@vrbo/react-event-tracking';
import SubmitButton from '../../Button/SubmitButton';
import { optStatus, getMarketingEmailOptStatus } from '../../../utils/NewsLetterUtil';
import AppActions from '../../../actions/app';
import log from '../../../utils/ClientLogger';
import { addressFields } from '../../../utils/AddressUtil';
import { getCookie } from '../../../utils/CookieUtil';
import { createInteractionEvent, createOutcomeEvent, setBrandCustomerId } from '../../../utils/EventProviders/EgClickstreamUtil';
import ExperimentUtil from '../../../utils/ExperimentUtil';
import {
    isConfirmAddressFeatureEnabled
} from '../../../utils/ExperimentsUtil';
import { validateEmailSync } from '../../../actions/graphQlSvc';
import LeadActions from '../../../actions/lead';
import { getFuzzyLatLng } from '../../../utils/LocationUtil';
import { EMAIL_DUP_ERROR_MESSAGE, PHONE_VALIDATION_ERROR_MESSAGE } from '../../../constants/common';
import { JOIN_SOURCES, JoinCtx } from '../../../contexts/join';
import { LEAD_PAGE } from '../../PageFlow/PageList';

export const UPDATE_CONTACT = gql`
mutation updateContact($contact: UpdatePartnerContactInput!) {
    updateContact(contact: $contact) {
        type,
        vrboContact {
            autoAuthUrl,
            userPublicUuid,
            newSupplier
        }
    }
}
`;

const fetcher = async (url, params) => {
    const response = await fetch(url, params);
    log('info', 'fetcher response', {
        url,
        status: response.status
    });
    if (response.status === 409) {
        return [new Error(EMAIL_DUP_ERROR_MESSAGE), null];
    }
    if (!response.ok) {
        return [new Error(`Register endpoint error. Status: ${response.status}`), null];
    }
    return [null, response.body];
};
export function LeadFormSubmit(props) {
    const {
        estimateButtonLabel,
        isFormDataValid,
        siteName,
        firstName,
        lastName,
        email,
        locale,
        password,
        phoneCountryCode,
        phoneCountryCodeDigits,
        phoneNumber,
        emailOptOut,
        setEntryTicketId,
        setAutoAuthUrl,
        setPublicUuid,
        setNewSupplier,
        setHasAdvertiserForSite,
        setUserLoggedIn,
        setLoggedInUserType,
        onSubmitError,
        clearAccountCreationError,
        pageFlow,
        fingerprint,
        setCreateAccountStart,
        setCreateAccountSuccess,
        setCreateAccountFail,
        duaid,
        isFrboFlow
    } = props;

    const [animate, setAnimate] = useState(false);
    const { trigger } = useContext(TrackingContext);
    const {
        createJoin,
        loading: joinLoading
    } = useContext(JoinCtx);

    useEffect(() => {
        setAnimate(joinLoading);
    }, [joinLoading]);

    const getOptObject = () => {
        const status = getMarketingEmailOptStatus(siteName);
        const isAddEmailOpt = (
            status === optStatus.OPT_IN_CHECKBOX
            || status === optStatus.OPT_OUT_CHECKBOX);
        return isAddEmailOpt ? { emailOptOut } : {};
    };

    const onUpdateCompleted = (updateResponse) => {
        const {
            type: contactType, vrboContact
        } = updateResponse.updateContact;
        setCreateAccountSuccess();
        switch (contactType) {
        case 'FRBO':
            // May need to update the below structure once the gql changes are merged
            setEntryTicketId(vrboContact.entryTicketId);
            setPublicUuid(vrboContact.userPublicUuid);
            setNewSupplier(vrboContact.newSupplier);
            setAutoAuthUrl(vrboContact.autoAuthUrl);

            // We can assume since the call was successful, we're logged in and have an advertiser
            setUserLoggedIn(true);
            setLoggedInUserType('OWNER');
            setHasAdvertiserForSite(true);

            trigger(
                'userAccountCreated',
                { publicuuid: vrboContact.userPublicUuid, ...getOptObject() }
            );
            setBrandCustomerId(vrboContact.userPublicUuid);
            trigger('outcome', createOutcomeEvent('user_account_creation.succeeded', 'LeadCapture', 'CreateAccount'));
            pageFlow.next();
            break;

        default:
            throw new Error(`Unknown contactType '${contactType}' returned by service`);
        }
    };

    const onError = (error) => {
        if (
            error.message === EMAIL_DUP_ERROR_MESSAGE
            || error.message === PHONE_VALIDATION_ERROR_MESSAGE
        ) {
            log('info', 'LeadFormSubmit-Info', {
                message: error.message
            });
        } else {
            log('error', 'LeadFormSubmit-Error', {
                message: error.message
            });
        }
        trigger('outcome', createOutcomeEvent('user_account_creation.failed', 'LeadCapture', 'CreateAccount'));
        setCreateAccountFail(error.message);
        onSubmitError(error.message);
        setAnimate(false);
    };
    const [updateContact] = useMutation(UPDATE_CONTACT, {
        onCompleted: onUpdateCompleted,
        onError
    });

    const handleSave = async () => {
        trigger('genericClick', {
            category: 'navigation',
            action: 'Lead: nextButton'
        });
        trigger('interaction', createInteractionEvent('user_account_creation.submitted', 'LeadCapture', 'CreateAccount'));
        setAnimate(true);
        clearAccountCreationError();
        setCreateAccountStart();
        log('info', 'email submitted', { inputEmail: email });
        let trustPayload = '';
        // better logic to determine if test or prod env
        let host = window.location.hostname;
        if (host.includes('local.')) {
            host = host.replace('local.', 'stage.');
        }
        const accountCreationEndpoint = `https://${host}/auth/aam/v3/register`;
        if (typeof window === 'object') {
            try {
                trustPayload = window?.trustApi?.getTrustPayload();
            } catch (e) {
                // Ignore error, user will never be FRBO in kumo.
            }
        }
        const gaData = {
            email,
            phoneCountryCode,
            phone: phoneNumber,
            event: 'PropertyContactSubmit',
            id: 'PropertyContactSubmit'
        };

        trigger('gtmEvent', gaData);

        if (!isFrboFlow) {
            await createJoin({
                source: JOIN_SOURCES.JOIN,
                pageName: LEAD_PAGE
            });
        } else {
            const data = {
                firstName,
                lastName,
                userName: email,
                secret: password,
                authType: 'HOMEAWAY'
            };
            // Call the identity endpoint to create a new traveler account for this email
            const [error, response] = await fetcher(accountCreationEndpoint, {
                method: 'POST',
                body: JSON.stringify(data),
                headers: {
                    'Content-Type': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'x-homeaway-site': siteName,
                    site: siteName
                }
            });
            if (response) {
                const contact = {
                    type: 'FRBO',
                    password: password || '',
                    phoneCountryCode: phoneCountryCode || '',
                    phoneCountryCodeDigits: phoneCountryCodeDigits || '',
                    phoneNumber: phoneNumber || '',
                    locale,
                    fingerprint,
                    trustPayload,
                    produceAccountCreatedEvent: true,
                    duaid,
                    email,
                    source: 'LEAD FORM'
                };

                // Call LYPS which will create advertiser for the contact that was created
                // by /auth/aam/v3/register
                updateContact({
                    variables: {
                        contact
                    }
                });
            }
            if (error) {
                onError(error);
            }
        }
    };

    const onSubmit = async () => {
        const {
            isDoubledValidation, isServerEmailValidationEnabled, setEmailValidationError
        } = props;
        try {
            await validateEmailSync(email, isDoubledValidation, isServerEmailValidationEnabled);
            handleSave();
        } catch (error) {
            setEmailValidationError({ type: LeadActions.VALIDATE_EMAIL_FAIL, error });
        }
    };

    return (
        <div className="ulx-form__buttonbox btn-fadein">
            <SubmitButton
                data-wdio="next-button"
                animated={animate}
                label={estimateButtonLabel}
                disabled={!isFormDataValid || animate}
                onClick={async () => { await onSubmit(); }}
                htmlButtonType="button"
            />
        </div>
    );
}

LeadFormSubmit.propTypes = {
    siteName: PropTypes.string.isRequired,
    estimateButtonLabel: PropTypes.string.isRequired,
    isFormDataValid: PropTypes.bool.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    locale: PropTypes.string,
    email: PropTypes.string.isRequired,
    password: PropTypes.string,
    phoneCountryCode: PropTypes.string.isRequired,
    phoneCountryCodeDigits: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string.isRequired,
    emailOptOut: PropTypes.bool.isRequired,
    setEntryTicketId: PropTypes.func.isRequired,
    setAutoAuthUrl: PropTypes.func.isRequired,
    setPublicUuid: PropTypes.func.isRequired,
    setNewSupplier: PropTypes.func.isRequired,
    setHasAdvertiserForSite: PropTypes.func.isRequired,
    setUserLoggedIn: PropTypes.func.isRequired,
    setLoggedInUserType: PropTypes.func.isRequired,
    onSubmitError: PropTypes.func.isRequired,
    clearAccountCreationError: PropTypes.func.isRequired,
    pageFlow: PropTypes.shape({
        next: PropTypes.func,
        nextPageLink: PropTypes.func
    }).isRequired,
    fingerprint: PropTypes.shape({
        orgId: PropTypes.string,
        deviceId: PropTypes.string
    }).isRequired,
    setCreateAccountStart: PropTypes.func.isRequired,
    setCreateAccountSuccess: PropTypes.func.isRequired,
    setCreateAccountFail: PropTypes.func.isRequired,
    address: PropTypes.shape({
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        stateProvince: PropTypes.string,
        postalCode: PropTypes.string,
        country: PropTypes.string
    }).isRequired,
    latLngDetails: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number,
        fuzzy: PropTypes.bool,
        fromMap: PropTypes.bool,
        fromGeo: PropTypes.bool
    }),
    isFrboFlow: PropTypes.bool.isRequired,
    duaid: PropTypes.string.isRequired,
    isDoubledValidation: PropTypes.bool,
    isServerEmailValidationEnabled: PropTypes.bool.isRequired,
    setEmailValidationError: PropTypes.func.isRequired
};
LeadFormSubmit.defaultProps = {
    password: '',
    locale: 'en_US',
    latLngDetails: {
        latitude: 0.0,
        longitude: 0.0
    },
    isDoubledValidation: false
};

/* istanbul ignore next */
const mapStateToProps = (state) => {
    const {
        app, joinDetails, location, google
    } = state;
    const { features } = app;
    const {
        propertyName, propertyType, unitCount, currency, managerInfo, chainInfo, latinPropertyName,
        competitorUrl
    } = joinDetails;

    const {
        address, addressValidation, latLng, serverAddressValidation, geocoderLatLng,
        partialMatch
    } = location;
    const { placeId } = google;

    const experimentUtil = new ExperimentUtil(app.experiments);
    const isAddressV2 = features.isConventionalLodgingFlow || isConfirmAddressFeatureEnabled(experimentUtil);

    const parseAddressForLatLng = () => {
        const fromMap = false;
        const fromGeo = true;

        const result = getFuzzyLatLng(
            isAddressV2,
            serverAddressValidation,
            latLng,
            geocoderLatLng,
            partialMatch
        );
        return {
            ...result,
            fromMap,
            fromGeo
        };
    };

    const isUnicode = !Object.keys(addressValidation).reduce(
        (acc, x) => acc && addressValidation[x].isLatin,
        true
    );
    const duaid = getCookie('DUAID');

    const isCasDeprecated = true;
    const latLngDetails = parseAddressForLatLng();
    return {
        competitorUrl,
        createAccountStarted: app.createAccount.start,
        siteName: app.siteName,
        propertyName,
        latinPropertyName,
        address: addressFields(address, features.useThreeLetterCountryCode),
        isUnicode,
        propertyType,
        unitCount,
        currency,
        managerInfo,
        chainInfo,
        latLngDetails,
        placeId,
        isCasDeprecated,
        duaid,
        isFrboFlow: features.isFrboFlow,
        partialMatch,
        isAddressV2
    };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) => ({
    setCreateAccountStart: () => dispatch(AppActions.setCreateAccountStart()),
    setCreateAccountSuccess: () => dispatch(AppActions.setCreateAccountSuccess()),
    setCreateAccountFail: (error) => dispatch(AppActions.setCreateAccountFail(error)),
    setEmailValidationError: (error) => dispatch(error)
});

export default connect(mapStateToProps, mapDispatchToProps)(LeadFormSubmit);
